import React, { useState, useRef, useEffect, forwardRef } from 'react';

interface ProgressState {
  playedSeconds: number;
  played: number;
  loadedSeconds: number;
  loaded: number;
}

export interface CustomReactPlayerProps {
  url: string;
  controls?: boolean;
  loop?: boolean;
  progressInterval?: number;
  width?: string | number;
  height?: string | number;
  className?: string;
  onProgress?: (state: ProgressState) => void;
  onEnded?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onDuration?: (duration: number) => void;
  onReady?: () => void;
  [x: string]: any;
}

export interface CustomReactPlayerMethods {
  getCurrentTime: () => number;
  getDuration: () => number;
  seekTo: (seconds: number) => void;
  play: () => Promise<void> | void;
  pause: () => void;
  getInternalPlayer: () => HTMLVideoElement | null;
}

const CustomReactPlayer = forwardRef<CustomReactPlayerMethods, CustomReactPlayerProps>(({
  url,
  controls = false,
  loop = false,
  progressInterval = 10,
  onProgress,
  onEnded,
  onPlay,
  onPause,
  onDuration,
  onReady,
  width = '100%',
  height = 'auto',
  className = '',
  ...props
}, ref) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [ready, setReady] = useState<boolean>(false);
  const progressIntervalRef = useRef<number | null>(null);

  // Initialize the video player with the ref
  useEffect(() => {
    const playerMethods: CustomReactPlayerMethods = {
      getCurrentTime: () => videoRef.current?.currentTime || 0,
      getDuration: () => videoRef.current?.duration || 0,
      seekTo: (seconds: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = seconds;
        }
      },
      play: () => videoRef.current?.play(),
      pause: () => videoRef.current?.pause(),
      getInternalPlayer: () => videoRef.current,
    };

    // Expose methods via ref
    if (ref) {
      if (typeof ref === 'function') {
        ref(playerMethods);
      } else {
        ref.current = playerMethods;
      }
    }
  }, [ref]);

  // Setup progress reporting
  useEffect(() => {
    if (videoRef.current && ready && onProgress) {
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
      }

      progressIntervalRef.current = window.setInterval(() => {
        const currentTime = videoRef.current?.currentTime || 0;
        const videoDuration = videoRef.current?.duration || 0;

        if (!isNaN(currentTime) && !isNaN(videoDuration) && videoDuration > 0) {
          const buffered = videoRef.current?.buffered;
          let loadedSeconds = 0;

          if (buffered && buffered.length > 0) {
            loadedSeconds = buffered.end(buffered.length - 1);
          }

          onProgress({
            playedSeconds: currentTime,
            played: currentTime / videoDuration,
            loadedSeconds,
            loaded: loadedSeconds / videoDuration
          });
        }
      }, progressInterval);
    }

    return () => {
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [onProgress, progressInterval, ready]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const handleDurationChange = (): void => {
    const videoDuration = videoRef.current?.duration || 0;
    if (onDuration) onDuration(videoDuration);
  };

  const handleLoadedMetadata = (): void => {
    setReady(true);
    if (onReady) onReady();
    handleDurationChange();
  };

  const handleEnded = (): void => {
    if (onEnded) onEnded();
  };

  const handlePlay = (): void => {
    if (onPlay) onPlay();
  };

  const handlePause = (): void => {
    if (onPause) onPause();
  };

  return (
    <div
      className={`custom-react-player ${className}`}
      id="video-player"
      style={{
        width,
        height,
        position: 'relative'
      }}
    >
      <video
        ref={videoRef}
        src={url}
        controls={controls}
        loop={loop}
        className="cursor-pointer"
        onLoadedMetadata={handleLoadedMetadata}
        onDurationChange={handleDurationChange}
        onEnded={handleEnded}
        onPlay={handlePlay}
        onPause={handlePause}
        {...props}
      />
    </div>
  );
});

CustomReactPlayer.displayName = 'CustomReactPlayer';

export default CustomReactPlayer;
